<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useDefaultStore } from './stores/defaultStore'
import { NConfigProvider, enUS, dateEnUS } from 'naive-ui';
import { useRoute } from 'vue-router';
import hljs from 'highlight.js/lib/core'
import json from 'highlight.js/lib/languages/json'
import { storeToRefs } from 'pinia';
import { useThemeStore } from './stores/themeStore';
import { appLoader } from './services/AppLoaderService';

hljs.registerLanguage('json', json)
hljs.registerLanguage('naive-log', () => ({
  contains: [
    {
      classNumber: 'number',
      begin: /\d+/
    }
  ]
}))

const { theme, themeOverrides, componentOptions } = storeToRefs(useThemeStore())
const route = useRoute()
const connecting = ref(true)
const { isInitialized, isInitializing } = storeToRefs(useDefaultStore())

function onPreloadError(event: Event) {
  event.preventDefault()
  window.location.reload()
}

onMounted(async () => {
  console.log('App.vue mounted')
  // Listen for vite preload errors, which occers when failing to load dynamic imports
  // https://vite.dev/guide/build.html#load-error-handling

  window.addEventListener('vite:preloadError', onPreloadError)

  try {
    await appLoader.initialize()
  }
  catch (error) {
    console.error('Startup error', error)
  }
  finally {
    connecting.value = false
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('vite:preloadError', onPreloadError)
})

watch(theme, (value) => {
  document.documentElement.setAttribute('data-theme', value ? 'dark' : 'light')
}, {
  immediate: true
})
</script>

<template>
  <n-config-provider
    :theme="theme"
    :theme-overrides="themeOverrides"
    :component-options="componentOptions"
    :hljs="hljs"
    :locale="enUS"
    :date-locale="dateEnUS"
  >
    <n-loading-bar-provider
      :loading-bar-style="{ loading: 'height: 6px;', error: 'height: 6px;' }"
    >
      <n-notification-provider>
        <n-message-provider>
          <div>
            <div v-if="route.meta.anonymous || route.name === 'error'">
              <RouterView />
            </div>
            <div v-else-if="!isInitialized" class="w-screen h-screen flex flex-col justify-center items-center gap-2">
              <n-spin></n-spin>
              <span v-if="connecting">Connecting</span>
              <span v-else-if="isInitializing">Initializing</span>
              <span v-else>Working</span>
            </div>
            <div v-else>
              <RouterView />
            </div>
          </div>
        </n-message-provider>
      </n-notification-provider>
    </n-loading-bar-provider>
  </n-config-provider>
</template>